import React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface ModalState {
  slackShare: { isOpen: boolean; reviewId: string | null };
  productSlackShare: { isOpen: boolean; productId: string | null };
  createTeam: boolean;
  oosDetail: { isOpen: boolean; orderId: string | null };
  infographic: boolean;
  testimonialAd: { isOpen: boolean; reviewId: string | null };
}

export type ThemeMode = 'light' | 'dark' | 'system';

interface UIState {
  modals: ModalState;
  toasts: Toast[];
  sidebarCollapsed: boolean;
  expandedSections: Record<string, boolean>;
  activeTab: string;
  isSettingsOpen: boolean;
  settingsTab: 'team' | 'account';
  userMenuOpen: boolean;
  themeMode: ThemeMode;
}

interface UIActions {
  // Modal actions
  openModal: <K extends keyof ModalState>(
    modal: K,
    data?: ModalState[K] extends { isOpen: boolean } ? Omit<ModalState[K], 'isOpen'> : never
  ) => void;
  closeModal: (modal: keyof ModalState) => void;
  closeAllModals: () => void;

  // Toast actions
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Navigation
  setActiveTab: (tab: string) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSection: (sectionId: string) => void;

  // Settings
  openSettings: (tab?: 'team' | 'account') => void;
  closeSettings: () => void;
  setSettingsTab: (tab: 'team' | 'account') => void;

  // User menu
  toggleUserMenu: () => void;
  closeUserMenu: () => void;

  // Theme
  setThemeMode: (mode: ThemeMode) => void;
  cycleTheme: () => void;
}

type UIStore = UIState & UIActions;

const initialModals: ModalState = {
  slackShare: { isOpen: false, reviewId: null },
  productSlackShare: { isOpen: false, productId: null },
  createTeam: false,
  oosDetail: { isOpen: false, orderId: null },
  infographic: false,
  testimonialAd: { isOpen: false, reviewId: null },
};

const initialState: UIState = {
  modals: initialModals,
  toasts: [],
  sidebarCollapsed: false,
  expandedSections: { cx: true }, // Accordion behavior - only one section open
  activeTab: 'dashboard',
  isSettingsOpen: false,
  settingsTab: 'team',
  userMenuOpen: false,
  themeMode: 'system',
};

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  root.setAttribute('data-theme', mode);
  if (mode === 'dark') {
    root.classList.add('dark');
  } else if (mode === 'light') {
    root.classList.remove('dark');
  } else {
    // System preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  }
}

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
  ...initialState,

  openModal: (modal, data) => {
    set((state) => ({
      modals: {
        ...state.modals,
        [modal]:
          typeof state.modals[modal] === 'boolean'
            ? true
            : { isOpen: true, ...data },
      },
    }));
  },

  closeModal: (modal) => {
    set((state) => ({
      modals: {
        ...state.modals,
        [modal]:
          typeof state.modals[modal] === 'boolean'
            ? false
            : { ...state.modals[modal], isOpen: false },
      },
    }));
  },

  closeAllModals: () => set({ modals: initialModals }),

  addToast: (toast) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const newToast: Toast = { ...toast, id };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Auto-remove after duration
    const duration = toast.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearToasts: () => set({ toasts: [] }),

  setActiveTab: (activeTab) => set({ activeTab }),

  toggleSidebar: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

  toggleSection: (sectionId) =>
    set((state) => {
      const isCurrentlyExpanded = state.expandedSections[sectionId];
      // Accordion behavior: close all sections, then open the clicked one (if it was closed)
      if (isCurrentlyExpanded) {
        // Closing the current section - just close it
        return {
          expandedSections: {
            ...state.expandedSections,
            [sectionId]: false,
          },
        };
      } else {
        // Opening a new section - close all others
        return {
          expandedSections: { [sectionId]: true },
        };
      }
    }),

  openSettings: (tab = 'team') =>
    set({ isSettingsOpen: true, settingsTab: tab }),

  closeSettings: () => set({ isSettingsOpen: false }),

  setSettingsTab: (settingsTab) => set({ settingsTab }),

  toggleUserMenu: () =>
    set((state) => ({ userMenuOpen: !state.userMenuOpen })),

  closeUserMenu: () => set({ userMenuOpen: false }),

  setThemeMode: (themeMode) => {
    applyTheme(themeMode);
    set({ themeMode });
  },

  cycleTheme: () => {
    const order: ThemeMode[] = ['light', 'dark', 'system'];
    const current = get().themeMode;
    const next = order[(order.indexOf(current) + 1) % order.length];
    applyTheme(next);
    set({ themeMode: next });
  },
    }),
    {
      name: 'tc-insights-ui',
      partialize: (state) => ({
        themeMode: state.themeMode,
        sidebarCollapsed: state.sidebarCollapsed,
        expandedSections: state.expandedSections,
      }),
      onRehydrateStorage: () => {
        return (state?: UIStore) => {
          if (state) {
            applyTheme(state.themeMode);
          }
        };
      },
    }
  )
);

// Listen for OS theme changes when in "system" mode
if (typeof window !== 'undefined') {
  applyTheme(useUIStore.getState().themeMode);
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    if (useUIStore.getState().themeMode === 'system') {
      applyTheme('system');
    }
  });
}

// Convenience hooks
export const useToast = () => {
  const addToast = useUIStore((state) => state.addToast);

  // Memoize to prevent new object references on every render
  // which was causing useCallback dependencies to change and trigger refetches
  return React.useMemo(
    () => ({
      success: (message: string) => addToast({ type: 'success', message }),
      error: (message: string) => addToast({ type: 'error', message }),
      warning: (message: string) => addToast({ type: 'warning', message }),
      info: (message: string) => addToast({ type: 'info', message }),
    }),
    [addToast]
  );
};
